import { addedValues, addsHours } from "./scopeLines";
import type { LineType, RevisedMap, ScopeLine } from "./types";
import type { ChangeOrderDraft } from "./useChangeOrderDraft";

export interface ChangeOrderBody {
  data: {
    type: "change-orders";
    attributes: Record<string, unknown>;
    relationships: {
      "parent-project": { data: { type: "projects"; id: string } };
    };
  };
}

interface SourceItem {
  type: LineType;
  id: string;
  quantity?: number;
  "override-hours"?: number;
  "project-resource-id"?: string;
}

// Only lines that add hours are sent, with their full added values (the
// change request line's own quantity and effort; the server's defaults differ
// per field, so both are explicit). Custom-hours lines send quantity only: the
// catalog prices them. The resource goes only when it changed.
export const sourceItemsFor = (
  lines: ScopeLine[],
  revised: RevisedMap
): SourceItem[] =>
  lines
    .filter((line) => addsHours(line, revised[line.key]))
    .map((line) => {
      const r = revised[line.key];
      const { quantity, effort } = addedValues(line, r);
      const item: SourceItem = { type: line.type, id: line.id, quantity };
      if (!line.customHours) item["override-hours"] = effort;
      if (r?.resourceId && r.resourceId !== line.resourceId)
        item["project-resource-id"] = r.resourceId;
      return item;
    });

export const governanceItemsFor = (governance: Record<string, number>) =>
  Object.entries(governance).map(([id, hours]) => ({ id, hours }));

const envelope = (
  attributes: Record<string, unknown>,
  parentProjectId: string
): ChangeOrderBody => ({
  data: {
    type: "change-orders",
    attributes,
    relationships: {
      "parent-project": { data: { type: "projects", id: parentProjectId } },
    },
  },
});

const scopeAttributes = (draft: ChangeOrderDraft, lines: ScopeLine[]) => ({
  "source-items": sourceItemsFor(lines, draft.revised),
  "governance-items": governanceItemsFor(draft.governance),
});

export const buildCreateBody = (
  draft: ChangeOrderDraft,
  lines: ScopeLine[],
  parentProjectId: string
): ChangeOrderBody => {
  const common = {
    cause: draft.cause || null,
    "cause-note": draft.causeNote || null,
    "external-reference": draft.externalReference || null,
    "external-url": draft.externalUrl.trim() || null,
  };
  const attributes =
    draft.mode === "tracked"
      ? {
          ...common,
          "reference-only": true,
          hours: draft.trackedHours.trim(),
          revenue: draft.trackedRevenue.trim(),
        }
      : { ...common, "reference-only": false, ...scopeAttributes(draft, lines) };
  return envelope(attributes, parentProjectId);
};

export const buildPreviewBody = (
  draft: ChangeOrderDraft,
  lines: ScopeLine[],
  parentProjectId: string
): ChangeOrderBody | null =>
  draft.mode === "tracked"
    ? null
    : envelope(
        {
          ...(draft.cause ? { cause: draft.cause } : {}),
          ...scopeAttributes(draft, lines),
        },
        parentProjectId
      );
